import React, { useCallback, useEffect, useState } from 'react'
import { Page } from '@shopify/polaris'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import useAuthenticatedFetch from '../../hooks/useAuthenticatedFetch'
import { APP_PATH } from '../../appConfig'
import './style.css'

const VIDEOS = [
    {
        title: 'Shopify shipping app not showing shipping rates on checkout',
        url: 'https://www.youtube.com/watch?v=nR34raPgyt8&list=PLkeebmoFziPwdGytDjJs51GuB3_OUzj5Q&index=2',
        thumbnail: 'https://i.ytimg.com/vi/nR34raPgyt8/hqdefault.jpg'
    },
    {
        title: 'How to add Shipping Rate',
        url: 'https://www.youtube.com/watch?v=JmR2i_qtWWw',
        thumbnail: 'https://i.ytimg.com/vi/JmR2i_qtWWw/hqdefault.jpg'
    }
]
const SLIDE_WIDTH = 296 // 280px card + 16px gap
const APPS_VISIBLE = 3

const Svg = ({ size = 20, children, ...rest }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...rest}>{children}</svg>
)
const ChevronLeft = () => <Svg size={14} strokeWidth="1.8"><path d="M12 5l-5 5 5 5" /></Svg>
const ChevronRight = ({ size = 14, color }) => <Svg size={size} strokeWidth="1.8" stroke={color || 'currentColor'}><path d="M8 5l5 5-5 5" /></Svg>
const PlayBadge = () => (
    <svg width="56" height="40" viewBox="0 0 56 40" role="img" aria-label="Play video">
        <rect width="56" height="40" rx="10" fill="#FF0000" />
        <path d="M22 12l14 8-14 8z" fill="#fff" />
    </svg>
)

const GOTO = [
    {
        title: 'Shipping Rates', text: 'View, filter and turn rates on or off.', cta: 'Open shipping rates', to: '/rates',
        iconBg: '#303030', stroke: '#fff', icon: 'M3 6h10v8H3zM13 9h3l2 2v3h-5M6 16a1.5 1.5 0 1 0 0-.01M15 16a1.5 1.5 0 1 0 0-.01'
    },
    {
        title: 'Free Shipping Rules', text: 'Minimum spend and product eligibility.', cta: 'Set up free shipping', to: '/free_shipping_rules',
        iconBg: '#E0F0FF', stroke: '#00527C', icon: 'M4 8h12v8H4zM3 5h14v3H3zM10 5v11M10 5c-1-2-4-2-4 0s4 0 4 0c1-2 4-2 4 0s-4 0-4 0'
    },
    {
        title: 'Fallback Shipping Rate', text: 'Shown when no other rate matches.', cta: 'Set fallback rate', to: '/default-price',
        iconBg: '#FEE9E8', stroke: '#C70A24', icon: 'M10 3l7 7-7 7-7-7zM10 7v6'
    }
]

const Home = () => {
    const fetchJson = useAuthenticatedFetch()
    const { shopName, store, warning, activePlan, remainingTrial, phone } = useSelector(state => state.ConfigReducer)
    const { shippingCounts, defaultPrice, free_shipping_rule } = useSelector(state => state.ShippingReducer)

    const [counts, setCounts] = useState({ active: null, draft: null })
    const [videoIndex, setVideoIndex] = useState(0)
    const [apps, setApps] = useState([])
    const [appIndex, setAppIndex] = useState(0)

    const getCount = useCallback(async active => {
        try {
            const res = await fetchJson(`/shipping?limit=1&offset=0&active=${active}`, { method: 'GET' })
            return res?.total ?? null
        } catch (e) {
            return null
        }
    }, [fetchJson])

    useEffect(() => {
        if (!shippingCounts) return
        Promise.all([getCount(true), getCount(false)]).then(([active, draft]) => setCounts({ active, draft }))
    }, [shippingCounts, getCount])

    useEffect(() => {
        if (!store?.domain) return
        fetchJson(`/applist?path=${APP_PATH}&shop=${store.domain}`, { method: 'GET' })
            .then(list => setApps(Array.isArray(list) ? list : []))
            .catch(() => setApps([]))
    }, [store?.domain, fetchJson])

    const ccsEnabled = !warning
    const fallbackConfigured = Boolean(defaultPrice?.id)

    const steps = [
        { title: 'Enable Carrier Calculated Shipping', text: 'Ask Shopify Support to turn on CCS for your store.', to: '/rates', done: ccsEnabled },
        { title: 'Add your first shipping rate', text: 'Set zones and price by order total, weight or quantity.', to: '/rates/new', done: shippingCounts > 0 },
        { title: 'Set a fallback rate', text: 'Shown at checkout when no other rate matches.', to: '/default-price', done: fallbackConfigured },
        { title: 'Offer free shipping', text: 'Free shipping over a minimum spend, with an optional schedule.', to: '/free_shipping_rules', done: Boolean(free_shipping_rule?.active) }
    ]

    const video = VIDEOS[videoIndex]
    const goVideo = i => setVideoIndex((i + VIDEOS.length) % VIDEOS.length)

    const maxAppIndex = Math.max(0, apps.length - APPS_VISIBLE)
    const goApp = i => setAppIndex(Math.max(0, Math.min(maxAppIndex, i)))

    const storeName = shopName || store?.shop_name || store?.domain?.replace('.myshopify.com', '') || ''

    let fallbackBadge = { cls: 'attention', label: 'Not-Configured' }
    if (fallbackConfigured) fallbackBadge = defaultPrice.active ? { cls: 'success', label: 'Active' } : { cls: 'neutral', label: 'Off' }

    return (
        <Page
            title={storeName ? `Welcome back, ${storeName}` : 'Welcome back'}
            subtitle="Manage your shipping rates, free shipping and checkout pricing from one place."
            primaryAction={{ content: 'Add Shipping', url: '/rates/new' }}
        >
            <div className="hd">
                {/* Status strip */}
                <section className="hd-status" aria-label="Store status">
                    <Link to="/rates" className="hd-card hd-tile">
                        <span className="hd-muted">Shipping rates</span>
                        <strong className="hd-tile-value">{shippingCounts}</strong>
                        <span className="hd-muted">
                            {counts.active !== null ? `${counts.active} active · ${counts.draft} draft` : ' '}
                        </span>
                    </Link>
                    <Link to="/rates" className="hd-card hd-tile">
                        <span className="hd-muted">Carrier Calculated Shipping</span>
                        <span className={`hd-badge ${ccsEnabled ? 'success' : 'critical'}`}>{ccsEnabled ? 'Enabled' : 'Not enabled'}</span>
                        <span className="hd-muted hd-font-12">Required to show rates at checkout</span>
                    </Link>
                    <Link to="/default-price" className="hd-card hd-tile">
                        <span className="hd-muted">Fallback shipping rate</span>
                        <span className={`hd-badge ${fallbackBadge.cls}`}>{fallbackBadge.label}</span>
                        <span className="hd-muted">Fallback when no rule matches</span>
                    </Link>
                    <a href={`https://admin.shopify.com/store/${storeName}/charges/${APP_PATH}/pricing_plans`} target="_top" className="hd-card hd-tile">
                        <span className="hd-muted">Current plan</span>
                        <strong className="hd-tile-plan">{activePlan?.name || '—'}</strong>
                        <span className="hd-muted">{remainingTrial > 0 ? `${remainingTrial} days left in trial` : ' '}</span>
                    </a>
                </section>

                <div className="hd-row">
                    {/* Setup guide */}
                    <section className="hd-card hd-guide">
                        <div className="hd-guide-head">
                            <h2 className="hd-h2">Setup guide</h2>
                            <p className="hd-muted">Finish these steps so customers see the right shipping options at checkout.</p>
                        </div>
                        {steps.map(step => (
                            <Link key={step.title} to={step.to} className="hd-step">
                                <span className={`hd-step-dot${step.done ? ' done' : ''}`} aria-hidden="true">
                                    {step.done ? <Svg size={12} stroke="#fff" strokeWidth="3"><path d="M4 10l4 4 8-8" /></Svg> : null}
                                </span>
                                <span className="hd-step-text">
                                    <strong>{step.title}</strong>
                                    <span className="hd-muted">{step.text}</span>
                                </span>
                                <ChevronRight size={16} color="#8a8a8a" />
                            </Link>
                        ))}
                    </section>

                    {/* Tutorials slider */}
                    <aside className="hd-tutorials">
                        <section className="hd-card">
                            <div className="hd-tut-head">
                                <h2 className="hd-h2">Tutorials</h2>
                                <span className="hd-muted">{videoIndex + 1} / {VIDEOS.length}</span>
                                <button type="button" className="hd-arrow" aria-label="Previous tutorial" onClick={() => goVideo(videoIndex - 1)}><ChevronLeft /></button>
                                <button type="button" className="hd-arrow" aria-label="Next tutorial" onClick={() => goVideo(videoIndex + 1)}><ChevronRight /></button>
                            </div>
                            <a href={video.url} target="_blank" rel="noreferrer" className="hd-thumb" style={{ backgroundImage: `url(${video.thumbnail})` }}>
                                <PlayBadge />
                            </a>
                            <div className="hd-tut-body">
                                <a href={video.url} target="_blank" rel="noreferrer" className="hd-link hd-tut-title">{video.title}</a>
                                <div className="hd-tut-foot">
                                    {VIDEOS.map((v, i) => (
                                        <button key={v.url} type="button" className={`hd-dot${i === videoIndex ? ' on' : ''}`} style={{ width: i === videoIndex ? 18 : 8 }} aria-label={`Show tutorial ${i + 1}`} onClick={() => goVideo(i)} />
                                    ))}
                                    <span style={{ flexGrow: 1 }} />
                                    <Link to="/tutorials" className="hd-link">See all</Link>
                                </div>
                            </div>
                        </section>
                    </aside>
                </div>

                {/* Go to */}
                <section className="hd-section">
                    <h2 className="hd-h2">Go to</h2>
                    <div className="hd-goto">
                        {GOTO.map(item => (
                            <Link key={item.title} to={item.to} className="hd-card hd-opt">
                                <span className="hd-opt-icon" style={{ background: item.iconBg }} aria-hidden="true">
                                    <Svg size={22} stroke={item.stroke} strokeWidth="1.7"><path d={item.icon} /></Svg>
                                </span>
                                <strong className="hd-opt-title">{item.title}</strong>
                                <span className="hd-opt-text">{item.text}</span>
                                <span className="hd-opt-cta">{item.cta} →</span>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Need help */}
                <section className="hd-help">
                    <Svg size={28} stroke="#fff" strokeWidth="1.5"><path d="M3 9a7 7 0 1 1 14 0v3a2 2 0 0 1-2 2h-1V9h3M3 9h3v5H5a2 2 0 0 1-2-2zM14 14c0 2-2 3-4 3" /></Svg>
                    <div className="hd-help-text">
                        <h2>Need help?</h2>
                        <p>Our team can help you set up rates for your store.</p>
                    </div>
                    <a href={`https://api.whatsapp.com/send/?phone=${phone}`} target="_blank" rel="noreferrer" className="hd-help-btn">Help Center</a>
                </section>

                {/* More apps */}
                {apps.length ? (
                    <section className="hd-section">
                        <div className="hd-apps-head">
                            <h2 className="hd-h2">More apps from our team</h2>
                            <button type="button" className="hd-arrow lg" aria-label="Previous apps" disabled={appIndex === 0} onClick={() => goApp(appIndex - 1)}><ChevronLeft /></button>
                            <button type="button" className="hd-arrow lg" aria-label="Next apps" disabled={appIndex === maxAppIndex} onClick={() => goApp(appIndex + 1)}><ChevronRight /></button>
                        </div>
                        <div className="hd-viewport">
                            <div className="hd-track" style={{ transform: `translateX(-${appIndex * SLIDE_WIDTH}px)` }}>
                                {apps.map(app => (
                                    <article key={app.link || app.name} className="hd-card hd-app">
                                        <div className="hd-app-head">
                                            {app.image ? <img src={app.image} alt="" className="hd-app-icon" /> : <span className="hd-app-icon" aria-hidden="true" />}
                                            <strong>{app.name}</strong>
                                        </div>
                                        <p>{app.description}</p>
                                        <a href={app.link} target="_blank" rel="noreferrer" className="hd-app-btn">Install</a>
                                    </article>
                                ))}
                            </div>
                        </div>
                        {maxAppIndex > 0 ? (
                            <div className="hd-dots">
                                {Array.from({ length: maxAppIndex + 1 }).map((_, i) => (
                                    <button key={i} type="button" className={`hd-dot${i === appIndex ? ' on' : ''}`} style={{ width: i === appIndex ? 20 : 8 }} aria-label={`Go to slide ${i + 1}`} onClick={() => goApp(i)} />
                                ))}
                            </div>
                        ) : null}
                    </section>
                ) : null}
            </div>
        </Page>
    )
}

export default Home
